import { useState, useMemo, useCallback } from 'react';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { customAlphabet } from 'nanoid';
import SortableRow from '../SortableRow';
import { useWindowSize } from '../../../../../utils/hooks/useWindowSize';
import { useContainerSize } from '../../../../../utils/hooks/useContainerSize';
import { useAuth } from '../../../../../utils/Auth';
import { isConnectButtonComplete } from '../../../../../lib/connectButtons';
import styles from './QrConnectRows.module.css';

export default function QrConnectRows() {
  const { qr_data, dataDispatch } = useAuth();
  const displayRows = useMemo(() => qr_data?.data?.connect?.buttons || [], [qr_data?.data?.connect?.buttons]);
  
  const dispatchConnect = useCallback((payload) => {
    dataDispatch({ type: 'connect', data: payload });
  }, [dataDispatch]);

  const nanoid = customAlphabet('1234567890abcdefghijklmnopqrstuvwxyzQWERTYUIOPASDFGHJKLZXCVBNM', 6);
  const [ containerRef, { width: containerWidth }] = useContainerSize();
  const { width: windowWidth } = useWindowSize();

  const [ allCollapsed, setAllCollapsed] = useState(false);
  const hasIncompleteRow = useMemo(() => displayRows.some((row) => !isConnectButtonComplete(row)), [displayRows]);
  const addButtonDisabled = displayRows.length >= 20 || hasIncompleteRow;
  const addButtonTitle = useMemo(() => {
    if (displayRows.length >= 20) {
      return "Maximum 20 buttons reached";
    }

    if (hasIncompleteRow) {
      return "Finish or delete the current draft button first";
    }

    return "Add new button";
  }, [displayRows.length, hasIncompleteRow]);

  const handleNewId = useCallback(() => {
    const newId = nanoid();
    if (displayRows.find(row => row.id === newId)) {
      return handleNewId();
    }
    return newId;
  }, [displayRows, nanoid]);

  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };

  const onDragEnd = useCallback((event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const activeIndex = displayRows.findIndex(row => row.id === active.id);
      const overIndex = displayRows.findIndex(row => row.id === over.id);
      const reorderedRows = reorder(displayRows, activeIndex, overIndex);
      const orderedIds = reorderedRows.map(row => row.id);
      dispatchConnect({ buttonsReorder: orderedIds });
    }
  }, [displayRows, dispatchConnect]);

  const handleAddRow = useCallback(() => {
    // Enforce maximum of 20 rows
    if (displayRows.length >= 20 || hasIncompleteRow) return;
    
    const newRow = {
      id: handleNewId(),
      color: '#000000',
      text: '',
      background: '#ffffff',
      type: '',
      value: '',
      icon: '',
      border: { style: 'none', color: '#000000', width: 1, radius: 8 },
      box_shadow: { x: 0, y: 1, blur: 3, color: '#dddddd' }
    };
    dispatchConnect({ buttonAdd: newRow });
  }, [displayRows.length, dispatchConnect, handleNewId, hasIncompleteRow]);

  const handleUpdateRow = useCallback(({ id, patch, removeKeys }) => {
    dispatchConnect({ buttonUpdate: { id, patch, removeKeys } });
  }, [dispatchConnect]);

  const handleDeleteRow = useCallback((rowId) => {
    // all rows are now deletable
    dispatchConnect({ buttonDelete: rowId });
  }, [dispatchConnect]);

  const rowIds = useMemo(() => displayRows.map(row => row.id), [displayRows]);

  return (
    <div className={styles.container} ref={containerRef}>
      <div className={styles.header}>
        <h5>Buttons</h5>
        <div className={styles.headerActions}>
          {(containerWidth <= 470 || windowWidth <= 768) && (
            <button className={styles.collapseAllButton} onClick={() => setAllCollapsed(true)} title="Collapse all buttons">
              <i className="fa-solid fa-compress" /> Collapse All
            </button>
          )}
          <button 
            className={styles.addButton} 
            onClick={handleAddRow} 
            disabled={addButtonDisabled}
            title={addButtonTitle}
          >
            <i className="fa-solid fa-plus" /> {containerWidth > 470 && 'Add Button'}
          </button>
        </div>
      </div>

      {displayRows.length > 0 ? (
        <DndContext collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <div className={styles.rowsContainer}>
            <SortableContext items={rowIds} strategy={verticalListSortingStrategy}>
              {displayRows.map(row => (
                <SortableRow
                  key={row.id}
                  id={row.id}
                  row={row}
                  onUpdate={handleUpdateRow}
                  onDelete={handleDeleteRow}
                  canDelete={true}
                  allCollapsed={allCollapsed}
                  setAllCollapsed={setAllCollapsed}
                  containerWidth={containerWidth}
                />
              ))}
            </SortableContext>
          </div>
        </DndContext>
      ) : (
        <div className={styles.emptyState}>
          <p>No buttons yet. Click "Add Button" to create one.</p>
        </div>
      )}
    </div>
  );
}
